import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import type { FileRejection } from "react-dropzone";
import { CloudArrowUpIcon, XMarkIcon } from "@heroicons/react/24/outline";
import type { FileUploadProps } from "../types/FileUpload.types";
import { cn } from "../utils/cn";

interface ExtendedFileUploadProps extends FileUploadProps {
  className?: string;
  showPreview?: boolean;
}

export const FileUpload: React.FC<ExtendedFileUploadProps> = ({
  accept,
  maxSize = 5 * 1024 * 1024, // 5MB default
  maxFiles = 1,
  onFilesSelected,
  disabled = false,
  label,
  helperText,
  className,
  showPreview = true,
}) => {
  const { t } = useTranslation();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setError(null);

      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        if (rejection.errors[0].code === "file-too-large") {
          setError(
            t("fileUpload.errors.fileTooLarge", {
              maxSize: (maxSize / 1024 / 1024).toFixed(1),
            })
          );
        } else if (rejection.errors[0].code === "file-invalid-type") {
          setError(t("fileUpload.errors.invalidType"));
        } else if (rejection.errors[0].code === "too-many-files") {
          setError(t("fileUpload.errors.tooManyFiles", { maxFiles }));
        }
        return;
      }

      setSelectedFiles(acceptedFiles);
      onFilesSelected(acceptedFiles);
    },
    [maxSize, maxFiles, onFilesSelected, t]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize,
    maxFiles,
    disabled,
  });

  const removeFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    onFilesSelected(newFiles);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className={cn("w-full", className)}>
      <div
        {...getRootProps()}
        className={cn(
          "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors duration-200",
          isDragActive
            ? "border-tiber-800 bg-tiber-50/5"
            : "border-tiber-300 hover:border-tiber-400 bg-white",
          disabled && "opacity-50 cursor-not-allowed",
          error && "border-red-500"
        )}
      >
        <input {...getInputProps()} />

        <CloudArrowUpIcon className="mx-auto h-12 w-12 text-tiber-400 mb-4" />

        <p className="text-base font-medium text-tiber-900 mb-1">
          {label || t("fileUpload.defaultLabel")}
        </p>

        {isDragActive ? (
          <p className="text-sm text-tiber-800">{t("fileUpload.dropHere")}</p>
        ) : (
          <p className="text-sm text-tiber-600">
            {t("fileUpload.dragDropOrClick")}
          </p>
        )}

        {helperText && (
          <p className="text-xs text-tiber-500 mt-2">{helperText}</p>
        )}
      </div>

      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}

      {showPreview && selectedFiles.length > 0 && (
        <div className="mt-4 space-y-2">
          {selectedFiles.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center justify-between p-3 bg-tiber-50 rounded-lg"
            >
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <div className="shrink-0">
                  {file.type.startsWith("image/") ? (
                    <img
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      className="h-10 w-10 rounded object-cover"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded bg-tiber-200 flex items-center justify-center">
                      <span className="text-xs font-medium text-tiber-700">
                        {file.name.split(".").pop()?.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-tiber-900 truncate">
                    {file.name}
                  </p>
                  <p className="text-xs text-tiber-600">
                    {formatFileSize(file.size)}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
                className="ml-3 shrink-0 text-tiber-400 hover:text-red-600 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
